import { Badge } from "@/components/ui/badge";
import type { MovementListRow } from "@/modules/inventory/domain/types";
import { receivePrimaryLabel } from "@/modules/inventory/presentation/lib/qty-unit-hint";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@/shared/ui/data-table";

const REASON_BADGE_VARIANTS: Record<
  MovementListRow["reason"],
  "default" | "secondary" | "destructive" | "outline"
> = {
  PURCHASE: "default",
  SALE: "secondary",
  WASTE: "destructive",
  ADJUST: "outline",
  TRANSFER_IN: "default",
  TRANSFER_OUT: "secondary",
};

const REASON_LABELS: Record<MovementListRow["reason"], string> = {
  PURCHASE: "ຮັບເຂົ້າ",
  SALE: "ຂາຍ",
  WASTE: "ສູນເສຍ",
  ADJUST: "ປັບຍອດ",
  TRANSFER_IN: "ໂອນເຂົ້າ",
  TRANSFER_OUT: "ໂອນອອກ",
};

type MovementsTableProps = {
  data: MovementListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onPaginationChange: (offset: number, limit: number) => void;
};

export function MovementsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onSortingChange,
  onPaginationChange,
}: MovementsTableProps) {
  const columns: TanstackReactTable.ColumnDef<MovementListRow>[] = [
    createSortableColumn<MovementListRow>("branchName", "ສາຂາ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.branchName}
        </span>
      ),
    }),
    createSortableColumn<MovementListRow>("lotDisplay", "Lot", {
      size: 220,
      cell: ({ row }) => (
        <span className="font-medium">{row.original.lotDisplay}</span>
      ),
    }),
    createSortableColumn<MovementListRow>("reason", "ປະເພດ", {
      size: 120,
      cell: ({ row }) => (
        <Badge
          variant={REASON_BADGE_VARIANTS[row.original.reason] ?? "outline"}
        >
          {REASON_LABELS[row.original.reason] ?? row.original.reason}
        </Badge>
      ),
    }),
    createSortableColumn<MovementListRow>("qtyDelta", "ປະລິມານ (+/-)", {
      size: 120,
      cell: ({ row }) => {
        const n = row.original.qtyDelta;
        const isPos = n >= 0;
        const view = receivePrimaryLabel(
          n,
          row.original.baseUnit,
          row.original.receiveUnit,
          row.original.receiveToBase,
        );
        const signed = `${isPos && n !== 0 ? "+" : ""}${view.text}`;
        return (
          <div
            className={
              isPos
                ? "text-green-600 dark:text-green-400"
                : "text-red-600 dark:text-red-400"
            }
          >
            <div>{signed}</div>
            {view.hint ? (
              <div className="text-muted-foreground text-xs">({view.hint})</div>
            ) : null}
          </div>
        );
      },
    }),
    {
      accessorKey: "refOrderId",
      header: "ອ້າງອີງອໍເດີ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.refOrderId ?? "–"}
        </span>
      ),
      size: 140,
      enableSorting: false,
    },
    createSortableColumn<MovementListRow>("createdAt", "ວັນທີ", {
      size: 160,
      cell: ({ row }) => formatDateTimeLocal(row.original.createdAt),
    }),
  ];

  return (
    <DataTable<MovementListRow, unknown>
      noDataMessage="ບໍ່ພົບ movement"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
    />
  );
}
